import { createContext, useContext, type CSSProperties, type ReactNode } from "react";
import { ResponsiveContainer, type TooltipContentProps, type TooltipValueType } from "recharts";

export type ChartConfig = Record<
  string,
  {
    color?: string;
    formatter?: (value: TooltipValueType | undefined) => ReactNode;
    label?: ReactNode;
  }
>;

interface ChartContainerProps {
  children: ReactNode;
  className?: string;
  config: ChartConfig;
  height?: number;
  minHeight?: number;
}

type ChartTooltipContentProps = Partial<TooltipContentProps<TooltipValueType, string | number>> & {
  config?: ChartConfig;
  hideLabel?: boolean;
  secondaryKey?: string;
};

interface ChartLegendContentProps {
  config?: ChartConfig;
  payload?: Array<{
    color?: string;
    dataKey?: string | number;
    value?: string | number;
  }>;
}

const ChartConfigContext = createContext<ChartConfig>({});

function joinClasses(...classes: Array<string | false | undefined>): string {
  return classes.filter(Boolean).join(" ");
}

function getPayloadKey(item: { dataKey?: unknown; name?: unknown; value?: unknown }): string {
  return String(item.dataKey ?? item.name ?? item.value ?? "");
}

function getConfigEntry(config: ChartConfig, item: { dataKey?: unknown; name?: unknown; value?: unknown }) {
  const key = getPayloadKey(item);
  return config[key] || config[String(item.name ?? "")] || config[String(item.value ?? "")];
}

function formatTooltipValue(value: TooltipValueType | undefined): ReactNode {
  if (Array.isArray(value)) {
    return value.join(" - ");
  }
  if (typeof value === "number") {
    return value.toLocaleString(undefined, { maximumFractionDigits: 1 });
  }
  return value;
}

function renderableLabel(value: unknown): ReactNode {
  return typeof value === "function" ? undefined : value as ReactNode;
}

export function ChartContainer({ children, className, config, height = 260, minHeight = 220 }: ChartContainerProps) {
  const colorVariables = Object.entries(config).reduce<Record<string, string>>((variables, [key, entry]) => {
    if (entry.color) {
      variables[`--color-${key}`] = entry.color;
    }
    return variables;
  }, {});
  const style = {
    ...colorVariables,
    "--chart-height": `${height}px`,
    "--chart-min-height": `${minHeight}px`
  } as CSSProperties;

  return (
    <ChartConfigContext.Provider value={config}>
      <div className={joinClasses("chart-container", className)} style={style}>
        <ResponsiveContainer width="100%" height="100%" minHeight={minHeight}>
          {children}
        </ResponsiveContainer>
      </div>
    </ChartConfigContext.Provider>
  );
}

export function ChartTooltipContent({
  active,
  config: overrideConfig,
  hideLabel,
  label,
  payload,
  secondaryKey
}: ChartTooltipContentProps) {
  const contextConfig = useContext(ChartConfigContext);
  const config = overrideConfig || contextConfig;
  const visiblePayload = payload?.filter((item) => !item.hide && item.value !== undefined && item.value !== null) || [];
  const secondary = secondaryKey ? visiblePayload[0]?.payload?.[secondaryKey] : undefined;

  if (!active || visiblePayload.length === 0) {
    return null;
  }

  return (
    <div className="chart-tooltip">
      {!hideLabel && label !== undefined && <div className="chart-tooltip__label">{label}</div>}
      <div className="chart-tooltip__items">
        {visiblePayload.map((item) => {
          const configEntry = getConfigEntry(config, item);
          const color = configEntry?.color || item.color || item.fill || item.stroke || "var(--color-text-muted)";
          const name = configEntry?.label || renderableLabel(item.name) || renderableLabel(item.dataKey);
          const value = configEntry?.formatter ? configEntry.formatter(item.value) : formatTooltipValue(item.value);

          return (
            <div className="chart-tooltip__item" key={`${String(item.dataKey)}-${String(item.name)}`}>
              <span className="chart-tooltip__indicator" style={{ background: color }} />
              <span>{name}</span>
              <strong>{value}</strong>
            </div>
          );
        })}
      </div>
      {secondary && <div className="chart-tooltip__secondary">{secondary}</div>}
    </div>
  );
}

export function ChartLegendContent({ config: overrideConfig, payload }: ChartLegendContentProps) {
  const contextConfig = useContext(ChartConfigContext);
  const config = overrideConfig || contextConfig;

  if (!payload?.length) {
    return null;
  }

  return (
    <div className="chart-legend">
      {payload.map((item) => {
        const configEntry = getConfigEntry(config, item);
        const color = configEntry?.color || item.color || "var(--color-text-muted)";
        const label = configEntry?.label || item.value;

        return (
          <span className="chart-legend__item" key={`${String(item.dataKey)}-${String(item.value)}`}>
            <i style={{ background: color }} />
            {label}
          </span>
        );
      })}
    </div>
  );
}
